import { HttpError, ResponseWithMethod, UNSAFE_formatHttpErrorForLlms } from '@luminovo/api-contracts';
import * as Sentry from '@sentry/core';

import { validateErrorCode } from './validateErrorCode';

const HANDLED_STATUSES = [401, 404, 406, 409];

/** Throws an HttpError for a failed response; statuses the app handles (401, 404, 406, 409) log as warnings, not errors. */
export async function assertSuccessfulResponse(
    response: ResponseWithMethod,
    endpoint: string,
    requestBody: unknown,
    /** Puts the body on the HttpError; opt in from `handleResponse`, the field says why it's off by default. */
    { attachResponseBody = false }: { attachResponseBody?: boolean } = {},
): Promise<Response> {
    if (!response.ok) {
        const { status, statusText } = response;
        // Never undefined, which would reach Sentry as an empty attribute; the endpoint names the method too.
        const method = response.method ?? endpoint.split(' ')[0];
        const clonedResponse = response.clone();
        const { code, receivedCode } = await validateErrorCode(response, endpoint);
        const responseBody = await clonedResponse.text();
        const sid = response.headers.get('x-epibator-sid') ?? undefined;
        const epibatorSha = response.headers.get('x-epibator-sha') ?? undefined;
        const luminovoVersion = response.headers.get('x-luminovo-version') ?? undefined;

        const level = HANDLED_STATUSES.includes(status) ? 'warn' : 'error';
        Sentry.logger[level](
            Sentry.logger.fmt`http.error endpoint=${endpoint} status=${status} method=${method} code=${code}`,
            {
                endpoint,
                method,
                status,
                code,
                // As sent: `code` is 'unknown' when the frontend doesn't know the code yet, and this still names it.
                received_code: receivedCode,
                // Edge errors carry no sid; leaving it off rather than sending '' keeps `has:sid` meaningful.
                ...(sid ? { sid } : {}),
            },
        );

        const error = new HttpError({
            code,
            status,
            statusText,
            endpoint,
            sid,
            epibatorSha,
            luminovoVersion,
            method,
            responseBody: attachResponseBody ? parseIfJson(responseBody) : undefined,
        });
        // oxlint-disable-next-line no-undef -- Vite replaces `process.env` with `{}` in the browser build; tests run in Node and read the real one
        if (process.env.CI || process.env.UNSAFE_ENABLE_HTTP_ERROR_FOR_LLMS) {
            throw UNSAFE_formatHttpErrorForLlms(error, requestBody, responseBody);
        }
        throw error;
    }
    return response;
}

/** Edge errors aren't JSON; return their raw text, where reading `.code` off a string would fail silently. */
function parseIfJson(body: string): unknown {
    try {
        return JSON.parse(body);
    } catch {
        return body;
    }
}
